/**
 * Global registry filters (F-13/F-14): status, stream, person, team, category,
 * priority, at-risk, overdue, search — synced with the query string so a
 * refresh or a shared link restores the view exactly, and applied by EVERY
 * view (a status picked on «Потоки» holds on «Исполнители»).
 *
 * The URL is the truth; this provider only reads/writes it. `page` is reset
 * (key removed → canonical page 1) whenever a filter changes.
 */

import { createContext, useContext, useMemo } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useQueryParams } from '../lib/query-params';

import { buildFilterContext, EMPTY_FILTERS, filtersToParams, hasActiveFilters, readFilters } from '../lib/filters';
import type { RegistryFilters } from '../lib/filters';
import type { Category, Person, RegistryStatus, Stream, Team } from '../api/types';
import { useRegistry } from '../api/planning-hooks';

export type FilterContextMaps = ReturnType<typeof buildFilterContext>;

interface FiltersContextValue {
  filters: RegistryFilters;
  /** Merges a patch into the URL and drops `page` (a filter change is page 1). */
  setFilters: (patch: RegistryFilters) => void;
  clear: () => void;
  isActive: boolean;
}

const FiltersContext = createContext<FiltersContextValue | null>(null);

export function FiltersProvider({ children }: { children: ReactNode }): ReactElement {
  const { params, setParams } = useQueryParams();

  const value = useMemo<FiltersContextValue>(() => {
    const filters = readFilters(params);
    return {
      filters,
      // A patch is a MERGE into the current filters (a chip sets one dimension
      // and must not clear the others); an explicit `undefined` in the patch
      // still clears that key. Any change drops `page` — page 3 of a search
      // that no longer exists is a broken state.
      setFilters: (patch) =>
        setParams({ ...filtersToParams({ ...filters, ...patch }), page: null }),
      // Removes only the FILTER keys (and page), never the view's own params
      // (sort, section…): clearing a filter must keep the rest of the view.
      clear: () => setParams({ ...filtersToParams(EMPTY_FILTERS), page: null }),
      isActive: hasActiveFilters(filters),
    };
  }, [params, setParams]);

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
}

export function useFilters(): FiltersContextValue {
  const value = useContext(FiltersContext);
  if (!value) throw new Error('useFilters must be used inside <FiltersProvider>');
  return value;
}

/**
 * Lookup maps built from the dictionaries (person name/team, stream name,
 * category name) + today's date. `useRegistry` is cached by React Query, so
 * calling this in several views is free. `teams` feeds the shared team filter
 * (F-19) in the FilterBar; `statuses` feeds the shared status filter — both
 * dictionaries drive the shared controls (F-23, 2026-10-14).
 */
export function useRegistryFilterContext(): {
  maps: FilterContextMaps | null;
  statuses: RegistryStatus[];
  people: Person[];
  streams: Stream[];
  teams: Team[];
  categories: Category[];
} {
  const { data } = useRegistry();
  return useMemo(() => {
    const people = data?.people ?? [];
    const streams = data?.streams ?? [];
    const teams = data?.teams ?? [];
    const categories = data?.categories ?? [];
    const statuses = data?.statuses ?? [];
    const maps = data ? buildFilterContext(people, streams, categories) : null;
    return { maps, statuses, people, streams, teams, categories };
  }, [data]);
}
